/**
 * Splitting a decimal angle into degrees–minutes–seconds or degrees–decimal-minutes parts,
 * with the rounding carry done once so 59.9996″ becomes 1′ 00.00″ rather than 60.00″.
 * Numbers stay numbers: the widget writes them out in the reader's own number format.
 */

import type { CoordinateAxis } from "@/lib/tool-kit/earth-science/coordinates";

export interface DmsParts {
  degrees: number;
  minutes: number;
  seconds: number;
}

export interface DdmParts {
  degrees: number;
  minutes: number;
}

/** Hemisphere letter for a signed value: N/S for latitude, E/W for longitude. */
export function hemisphereLetter(value: number, axis: CoordinateAxis): string {
  if (axis === "lat") {
    return value < 0 ? "S" : "N";
  }
  return value < 0 ? "W" : "E";
}

/** Degrees, whole minutes and seconds of |value|, seconds rounded to `decimals`. */
export function splitDms(value: number, decimals: number): DmsParts {
  const scale = 10 ** decimals;
  const totalSeconds = Math.round(Math.abs(value) * 3600 * scale) / scale;
  const degrees = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds - degrees * 3600) / 60);
  const seconds =
    Math.round((totalSeconds - degrees * 3600 - minutes * 60) * scale) / scale;
  return { degrees, minutes, seconds };
}

/** Degrees and decimal minutes of |value|, minutes rounded to `decimals`. */
export function splitDdm(value: number, decimals: number): DdmParts {
  const scale = 10 ** decimals;
  const totalMinutes = Math.round(Math.abs(value) * 60 * scale) / scale;
  const degrees = Math.floor(totalMinutes / 60);
  const minutes = Math.round((totalMinutes - degrees * 60) * scale) / scale;
  return { degrees, minutes };
}
